import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Recycle, Share2, PackageX, Eye } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, waOrderLink } from "../lib/api";
import api from "../lib/api";
import ProductDialog from "./ProductDialog";
import ShareDialog from "./ShareDialog";

export default function ProductCard({ product, index }) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    const onMove = (e) => {
        const el = e.currentTarget;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - r.left}px`);
        el.style.setProperty("--my", `${e.clientY - r.top}px`);
    };

    return (
        <>
            <motion.article
                data-testid={`product-card-${product.id}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: Math.min(index, 7) * 0.06 }}
                whileHover={{ y: -6 }}
                onClick={() => setOpen(true)}
                className="group cursor-pointer rounded-[26px] border-2 border-brand-ink bg-white overflow-hidden shadow-hard-sm hover:shadow-hard transition-shadow flex flex-col"
            >
                <div className="relative aspect-square overflow-hidden" onMouseMove={onMove}>
                    <img
                        src={product.image}
                        alt={product.title}
                        loading="lazy"
                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${product.sold_out ? "opacity-70 saturate-[0.6]" : ""}`}
                    />
                    <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                        style={{ background: "radial-gradient(240px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.30), transparent 70%)" }}
                    />
                    <span className="absolute top-3.5 left-3.5 px-3 py-1 rounded-full border-2 border-brand-ink bg-white text-[11px] font-extrabold">
                        {product.category}
                    </span>
                    {product.sold_out && (
                        <span data-testid={`product-soldout-badge-${product.id}`} className="absolute top-3.5 right-3.5 px-3 py-1 rounded-full border-2 border-brand-ink bg-brand-tomato text-brand-cream text-[11px] font-display font-extrabold tracking-wide shadow-hard-sm">
                            SOLD OUT
                        </span>
                    )}
                    {!product.sold_out && (
                        <span className="absolute top-3.5 right-3.5 px-3 py-1 rounded-full border-2 border-brand-ink bg-brand-sunshine font-display font-extrabold text-sm shadow-hard-sm">
                            {formatIDR(product.price)}
                        </span>
                    )}
                </div>

                <div className="p-5 flex flex-col gap-2 flex-1">
                    <h3 className="font-display text-lg font-bold leading-snug line-clamp-2">{product.title}</h3>
                    {product.material && (
                        <p className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-forest">
                            <Recycle className="w-3.5 h-3.5" strokeWidth={2.6} /> {product.material}
                        </p>
                    )}
                    <div className="mt-auto pt-3 flex flex-col gap-2.5">
                        <div className="flex items-center justify-between gap-2 min-w-0">
                            {product.creator_name ? (
                                <Link
                                    to={`/kreator/${encodeURIComponent(product.creator_name)}`}
                                    data-testid={`product-creator-link-${product.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-2 text-xs font-bold text-brand-ink/60 hover:text-brand-forest transition-colors min-w-0"
                                >
                                    <span className="w-6 h-6 rounded-full bg-brand-carrot text-white grid place-items-center font-display text-[11px] shrink-0">
                                        {product.creator_name?.[0]?.toUpperCase()}
                                    </span>
                                    <span className="truncate">{product.creator_name}</span>
                                </Link>
                            ) : (
                                <span className="text-xs font-bold text-brand-ink/40">Acture.id</span>
                            )}
                            <button
                                data-testid={`product-share-button-${product.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShareOpen(true);
                                }}
                                aria-label="Bagikan karya"
                                className="p-2.5 rounded-full border-2 border-brand-ink bg-white hover:bg-brand-sunshine/60 transition-colors shrink-0"
                            >
                                <Share2 className="w-4 h-4" strokeWidth={2.6} />
                            </button>
                        </div>
                        <div className="flex items-stretch gap-2">
                            <button
                                data-testid={`product-detail-button-${product.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(true);
                                }}
                                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-brand-sunshine text-brand-ink font-bold text-xs shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0"
                            >
                                <Eye className="w-4 h-4" strokeWidth={2.6} /> Detail
                            </button>
                            {product.sold_out ? (
                                <span
                                    data-testid={`product-soldout-label-${product.id}`}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-brand-ink/15 text-brand-ink font-bold text-xs cursor-not-allowed"
                                >
                                    <PackageX className="w-4 h-4" strokeWidth={2.6} /> Stok Habis
                                </span>
                            ) : (
                                <a
                                    data-testid={`product-wa-order-button-${product.id}`}
                                    href={waOrderLink(product.title, product.price, product.phone)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-brand-wa text-white font-bold text-xs shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 active:shadow-hard-sm"
                                >
                                    <MessageCircle className="w-4 h-4" strokeWidth={2.6} /> Order via WA
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </motion.article>

            <ProductDialog product={product} open={open} onOpenChange={setOpen} />
            <ShareDialog product={product} open={shareOpen} onOpenChange={setShareOpen} />
        </>
    );
}
